/**
 * Reference-counted body scroll lock.
 * Nested modals / drawers must not restore `overflow: hidden` after the last unlock
 * (the classic "can tap, cannot scroll" failure in the native WebView shell).
 */

let lockCount = 0;
let previousOverflow: string | null = null;
let previousHtmlOverflow: string | null = null;

export function lockBodyScroll(): () => void {
  if (typeof document === 'undefined') {
    return () => undefined;
  }

  if (lockCount === 0) {
    previousOverflow = document.body.style.overflow;
    previousHtmlOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = 'hidden';
    // iOS WKWebView sometimes scrolls the documentElement instead of body.
    document.documentElement.style.overflow = 'hidden';
  }
  lockCount += 1;

  let released = false;
  return () => {
    if (released) return;
    released = true;
    unlockBodyScroll();
  };
}

/** Canonical name used by Modal, GlobalSearch, DirectoryBrowseLayout, etc. */
export const acquireBodyScrollLock = lockBodyScroll;

export function unlockBodyScroll(): void {
  if (typeof document === 'undefined') return;
  if (lockCount <= 0) {
    lockCount = 0;
    return;
  }
  lockCount -= 1;
  if (lockCount > 0) return;

  document.body.style.overflow = previousOverflow ?? '';
  document.documentElement.style.overflow = previousHtmlOverflow ?? '';
  previousOverflow = null;
  previousHtmlOverflow = null;
}

/** Hard reset after navigation / shell resume when a lock may have leaked. */
export function forceUnlockBodyScroll(): void {
  if (typeof document === 'undefined') return;
  lockCount = 0;
  previousOverflow = null;
  previousHtmlOverflow = null;
  document.body.style.overflow = '';
  document.documentElement.style.overflow = '';
  if (document.body.style.touchAction === 'none') {
    document.body.style.touchAction = '';
  }
  if (document.documentElement.style.touchAction === 'none') {
    document.documentElement.style.touchAction = '';
  }
}

/**
 * If overflow is stuck hidden but no lock is held (leak / race), clear it.
 * Safe to call on every SPA navigation.
 */
export function repairBodyScrollIfLeaked(): void {
  if (typeof document === 'undefined') return;
  if (lockCount > 0) return;
  const bodyHidden = document.body.style.overflow === 'hidden';
  const htmlHidden = document.documentElement.style.overflow === 'hidden';
  if (bodyHidden || htmlHidden) {
    forceUnlockBodyScroll();
  }
}

export function getBodyScrollLockCount(): number {
  return lockCount;
}

/** Native shell injects recover calls against this global when overlays dismiss / app resumes. */
declare global {
  interface Window {
    __biltForceReleaseBodyScrollLocks?: () => void;
  }
}

if (typeof window !== 'undefined') {
  window.__biltForceReleaseBodyScrollLocks = forceUnlockBodyScroll;
}
